import { Role } from 'API';

/**
 * SEE-946: single source of truth for per-role routing concerns.
 *
 * Why this file exists:
 *   Pre-SEE-946 there were three roles and the navigation menu encoded each
 *   role's accessible pages inline via ad-hoc boolean expressions on
 *   `hasAccess`. With two new admin-tier roles the matrix is no longer
 *   trivially derivable from boolean flags — Secondary and Approval both land
 *   on `/overview/transactions` (SEE-1081 moved Secondary there, with a
 *   transactions-only Overview surface), Cardholder lands on
 *   `/overview/my-transactions`, etc. Centralising these per-role decisions here keeps:
 *     - the nav menu's Home-icon visibility,
 *     - RequireAuth's post-login redirect,
 *     - RoleRouteGuard's "where do I send a blocked user" fallback, and
 *     - landingPageHelper's resolution
 *   in lockstep. Update this file when a role's *page surfaces* change;
 *   per-action capability checks stay in `useUserRole.ts`.
 *
 * Fail-open contract (CRITICAL — preserves no-regression for non-flag paidols):
 *   This map's entries for SECONDARY_ADMINISTRATOR and APPROVAL_ADMINISTRATOR
 *   only kick in when the AppConfig flag `additionalAdminRolesEnabled` is true
 *   for the paidol. Consumers MUST pass the *effective* role (via
 *   `useUserRole`'s composites) rather than the raw DDB value, so that users
 *   carrying the new role values on a flag-OFF paidol are treated as
 *   Administrator and see no change.
 */

export interface RoleAccess {
  /**
   * The route a freshly-signed-in user of this role should land on when no
   * saved preference exists. Also the redirect target when a `RoleRouteGuard`
   * blocks them from a page.
   */
  defaultLanding: string;
  /**
   * Whether the left-sidebar "Home" (Overview) icon is visible. Secondary
   * admins have no Overview surface at all per the matrix; everyone else does.
   */
  showsHomeIcon: boolean;
}

/**
 * Per-role routing config. Roles not in this map fall back to CARDHOLDER's
 * entry — defensive default so a future enum addition can't crash the app.
 *
 * Implementation note: keyed by the string values of the Role enum rather
 * than computed properties (`[Role.ADMINISTRATOR]: ...`). Several jest
 * suites partially-mock the API module without including Role, which would
 * leave the computed-property form referencing `undefined` at module load
 * and crash every test that transitively imports useUserRole. The Role
 * enum's values are string literals matching the keys below, so the lookup
 * `ROLE_ACCESS[someRoleValue]` works identically either way.
 */
export const ROLE_ACCESS: Record<Role, RoleAccess> = {
  ADMINISTRATOR: {
    defaultLanding: '/overview',
    showsHomeIcon: true,
  },
  SECONDARY_ADMINISTRATOR: {
    // SEE-1081 ([Whitaker]): Secondary admins gained transaction visibility +
    // review, and reviewing/exporting transactions is their primary day-to-day,
    // so they land on the transactions queue (like Approval admins). The
    // Overview surface exposes only the Transactions tab; Welcome/insights/
    // personal surfaces stay gated to other roles. They retain full access to
    // Cards/Settings/Team via the nav.
    defaultLanding: '/overview/transactions',
    showsHomeIcon: true,
  },
  APPROVAL_ADMINISTRATOR: {
    // Their primary work is the transactions queue — land them there directly,
    // skipping the Welcome page (no onboarding checklist applies to them).
    defaultLanding: '/overview/transactions',
    showsHomeIcon: true,
  },
  GROUP_ADMINISTRATOR: {
    defaultLanding: '/overview/transactions',
    showsHomeIcon: true,
  },
  CARDHOLDER: {
    defaultLanding: '/overview/my-transactions',
    showsHomeIcon: true,
  },
  MEMBER: {
    defaultLanding: '/overview/my-transactions',
    showsHomeIcon: true,
  },
};

const FALLBACK_ACCESS: RoleAccess = ROLE_ACCESS.CARDHOLDER;

/**
 * Returns the role's routing config, defaulting to the Cardholder entry when
 * the role is unknown (e.g. a transient null during the initial Redux load).
 * Callers should still defensively check that userRole is non-null before
 * trusting the returned `defaultLanding`.
 */
export function getRoleAccess(role: Role | null | undefined): RoleAccess {
  if (!role) return FALLBACK_ACCESS;
  return ROLE_ACCESS[role] ?? FALLBACK_ACCESS;
}
